import type { BundleAssignmentDecision } from './ap/bundle-assignment.js';
import type { ApDocumentBillAttachment } from './ap/documents.js';
import type { IntakeDecision } from './ap/intake-rules.js';
import type { PipefyFieldAttachmentIdentity } from './external/pipefy.js';
import type { DocumentStatus } from './generated/enums.js';

/**
 * Channel Processing Types
 *
 * Generic types for multi-channel message processing (email, WhatsApp, SMS, etc.)
 * Used by both API and Worker for triage, classification, and document creation.
 */

/**
 * Supported channel types for message processing
 */
export const ChannelType = {
  EMAIL: 'EMAIL',
  WHATSAPP: 'WHATSAPP',
  SMS: 'SMS',
} as const;
export type ChannelType = (typeof ChannelType)[keyof typeof ChannelType];

/**
 * Job data for channel triage queue
 */
export interface ChannelTriageJobData {
  /** Type of channel */
  channelType: ChannelType;
  /** ID of the message record (EmailMessage, WhatsAppMessage, etc.) */
  messageId: string;
  /** Tenant ID for multi-tenant context */
  tenantId: string;
  /** Submission bundle that groups this message with its documents. */
  bundleId?: string;
  /** Entity ID associated with the message */
  entityId: string;
  /** Optional: ID of the inbox/channel configuration */
  inboxId?: string;
}

/**
 * Classification result from AI triage
 */
export interface ChannelTriageResult {
  /** Detected document type */
  documentType: string;
  /** Classification confidence (0-1) */
  confidence: number;
  /** Detected objective/intent */
  objective: string;
  /** Whether the message appears to be spam */
  isSpam: boolean;
  /** Reason for spam classification (if isSpam is true) */
  spamReason?: string;
  /** Detected language */
  language?: string;
  /** Summary of the message content */
  summary?: string;
  /** Extracted metadata (vendor name, invoice number, etc.) */
  extractedMetadata?: Record<string, unknown>;
}

/**
 * Generic message record interface for channel-specific records
 */
export interface ChannelMessageRecord {
  /** Message ID */
  messageId: string;
  /** Tenant ID */
  tenantId: string;
  /** Entity ID */
  entityId: string;
  /** Message status */
  status: string;
  /** Sender address */
  senderAddress: string;
  /** Subject (if applicable) */
  subject?: string | null;
  /** Body text */
  bodyText?: string | null;
  /** Body HTML */
  bodyHtml?: string | null;
  /** Attachments metadata */
  attachments?: unknown;
  /** Classification result (if processed) */
  classificationResult?: unknown;
}

/**
 * Attachment metadata for document creation
 */
export interface ChannelAttachmentForProcessing {
  /** Unique attachment ID */
  id: string;
  /** Original filename */
  filename: string;
  /** MIME type */
  mimeType: string;
  /** Size in bytes */
  size: number;
  /** Blob storage path */
  blobPath: string;
  /** BLAKE3 hash */
  blake3: string;
  /** SHA256 hash */
  sha256: string;
  /** Document type from pre-classification at triage (DocumentTypeClassifier) */
  classifiedType?: string;
  /** Confidence for classifiedType (0–1) */
  classifiedConfidence?: number;
  /** Vendor tax ID (CNPJ/CPF) extracted by pre-classifier */
  classifiedVendorTaxId?: string;
  /** Vendor name extracted by pre-classifier */
  classifiedVendorName?: string;
  /**
   * Source URL, when the attachment was downloaded from a link in the message body
   * (Pipefy signed URLs) rather than received as a native attachment. Lets a
   * reprocessed message recognise what it has already fetched.
   */
  sourceUrl?: string;
  /**
   * Stable Pipefy identity (card, configured attachment field, storage path) when the
   * file was recovered from the card's configured attachment field instead of the
   * e-mail. Signed download URLs expire, so this — never the URL — identifies the file.
   */
  pipefyFieldAttachment?: PipefyFieldAttachmentIdentity;
}

/** Status of one attachment in a multi-attachment email processing plan */
export type AttachmentPlanItemStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'LINKED' | 'FAILED';

/** One row in the email attachment processing plan */
export interface AttachmentPlanItem {
  attachmentIndex: number;
  /** ap_document.document_id created at triage */
  documentId: string;
  classifiedType: string;
  confidence: number;
  /** Lower = earlier in pipeline */
  priority: number;
  status: AttachmentPlanItemStatus;
  /** true → DOCUMENT_PROCESSING; false → link-only after bills exist */
  processable: boolean;
  /** Bill created or linked from this document (processable path) */
  resultBillId?: string;
  /** For link-only docs: bill they were linked to */
  linkedToBillId?: string;
  /** Vendor tax ID from pre-classification, passed through to document processor */
  classifiedVendorTaxId?: string;
  /** Vendor name from pre-classification, passed through to document processor */
  classifiedVendorName?: string;
  error?: string;
}

/** Persisted on DocumentBundle.processingPlan for submission-level orchestration. */
export interface AttachmentProcessingPlan {
  version: 1;
  items: AttachmentPlanItem[];
  createdAt: string;
  completedAt?: string;
}

/**
 * True while a v1 attachment plan (`EmailMessage.processingPlan`) still has jobs to run: it has
 * no `completedAt`. The one rule for the triage's final status, the final-reply sweep and the
 * no-bill retry guard (ALC-4465 F6). Anything that is not a v1 plan is not unfinished.
 */
export function attachmentPlanUnfinished(plan: unknown): boolean {
  if (typeof plan !== 'object' || plan === null || Array.isArray(plan)) return false;
  const record = plan as Record<string, unknown>;
  return record.version === 1 && Array.isArray(record.items) && !record.completedAt;
}

/**
 * Bundle plan high-level status. The current implementation only covers step 1
 * (classification + extraction). `READY` marks the terminal state when both
 * stages finish successfully and a downstream processor can take over.
 */
export type BundlePlanBundleStatus =
  'CLASSIFYING' | 'EXTRACTING' | 'READY' | 'FAILED' | 'CANCELLED';

export type BundlePlanClassificationStatus = 'PENDING' | 'AWAITING_PASSWORD' | 'DONE' | 'FAILED';

export type BundlePlanExtractionStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'SKIPPED' | 'FAILED';

export type BundlePlanAssignmentStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'FAILED';

export type BundleProcessingFailureStage =
  'CLASSIFICATION' | 'EXTRACTION' | 'PREASSIGNMENT' | 'ASSIGNMENT' | 'CANCELLED';

export type BundlePreassignmentFailureCode =
  | 'VENDOR_TAX_ID_COUNTRY_CONFLICT'
  | 'PREASSIGNMENT_FAILED'
  | 'NO_VENDOR_OR_AMOUNT'
  | 'PROCESSING_STALLED';

export const BundleEntityResolutionMethod = {
  TAX_ID: 'TAX_ID',
  TAX_ID_ROOT: 'TAX_ID_ROOT',
  NAME: 'NAME',
  /**
   * The AI's sure pick among a shared inbox's entities (2026-10-24). Elsewhere the AI only
   * suggests; plans created before 2026-09-07 also carry it.
   */
  LLM: 'LLM',
  TENANT_DEFAULT: 'TENANT_DEFAULT',
  CHANNEL_DEFAULT: 'CHANNEL_DEFAULT',
  INTAKE_RULE: 'INTAKE_RULE',
  /** A person chose the company on the no-bill card ("Escolher empresa"). */
  USER_CHOICE: 'USER_CHOICE',
} as const;
export type BundleEntityResolutionMethod =
  (typeof BundleEntityResolutionMethod)[keyof typeof BundleEntityResolutionMethod];

export interface BundleEntitySuggestion {
  entityId: string;
  confidence: number;
  evidence: string;
}

export interface BundleEntityResolution {
  status: 'PENDING' | 'DONE';
  entityId: string;
  method: BundleEntityResolutionMethod;
  defaultEntityId?: string | null;
  evidence?: string | null;
  taxIdMatchStatus?: 'NO_TAX_ID' | 'MATCHED' | 'NO_MATCH' | 'AMBIGUOUS';
  suggestion?: BundleEntitySuggestion | null;
  /** See `BundleEntityResolutionResult.needsEntityChoice`. */
  needsEntityChoice?: true;
}

export const MANUAL_BILL_CREATION_REQUIRED = 'MANUAL_BILL_CREATION_REQUIRED';

/**
 * v2 state machine for `DocumentBundle.processingPlan` (AP bundle orchestrator).
 * Survives worker restarts; mutations run under a Postgres advisory lock per bundle.
 */
export interface BundleProcessingPlan {
  version: 2;
  status: BundlePlanBundleStatus;
  items: {
    documentId: string;
    itemIndex: number;
    classification: {
      status: BundlePlanClassificationStatus;
      type?: string;
      vendorTaxId?: string;
      vendorName?: string;
      confidence?: number;
      error?: string;
    };
    extraction: {
      status: BundlePlanExtractionStatus;
      error?: string;
    };
  }[];
  context: {
    status: 'PENDING' | 'DONE';
    userInstructions?: string;
    intakeDecision?: IntakeDecision;
    entityResolution?: BundleEntityResolution;
    /**
     * The bundle waits for someone to choose its company (shared inbox). Every classify job of
     * the bundle parks on it, so one AI answer decides for all documents. A new retry (another
     * `retryRequestedAt`) or a chosen company lifts it.
     */
    entityChoiceParked?: { retryRequestedAt: string | null };
  };
  assignment?: {
    status: BundlePlanAssignmentStatus;
    updatedAt: string;
    error?: string;
    failures?: BundleAssignmentFailure[];
    decision?: BundleAssignmentDecision;
    /** FAILED only: no retry is coming (see `MarkBundleAssignmentFailedInput.final`). */
    final?: boolean;
  };
  /** Terminal failures before assignment, scoped to this submission rather than the shared document. */
  preassignmentFailures?: {
    documentId: string;
    code?: BundlePreassignmentFailureCode;
    error: string;
    registeredCountry?: string;
    documentCountry?: string;
  }[];
  cancellation?: {
    cancelledAt: string;
    cancelledByUserId: string;
    cancelledDocuments: number;
  };
  createdAt: string;
  updatedAt: string;
}

export type BundleProcessingUiStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type BundleProcessingStepStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'FAILED';

export interface BundleProcessingDocumentSummary {
  documentId: string;
  originalName: string;
  fileSizeBytes: number;
  processingStatus: DocumentStatus;
  billId?: string;
  /** Present when linked bill has a vendor snapshot (joined for bundle status UI). */
  billVendorName?: string;
  /** Linked bill total as decimal string when available. */
  billTotalAmount?: string;
  /** ISO currency when bill totals exist (typically BRL). */
  billCurrencyCode?: string;
  /**
   * How this document ended up on the linked bill (`ApDocument.metadata.billAttachmentType`),
   * when the worker persisted it at link time.
   */
  billAttachment?: ApDocumentBillAttachment;
  failureStage?: BundleProcessingFailureStage;
  failureCode?: BundlePreassignmentFailureCode;
  failureDetails?: { registeredCountry?: string; documentCountry?: string };
  failureReason?: string;
  /** Present when the bundle intentionally skipped this item, for example after UNKNOWN classification. */
  skippedReason?: string;
  /** Whether the UI can retry this skipped document by providing a manual AP document type. */
  canRetryWithDocumentTypeOverride?: boolean;
  /** Whether processing is paused until the user or integrator provides the PDF password. */
  requiresPassword?: boolean;
}

export type BundleProcessingStepName =
  | 'CLASSIFY_DOCUMENTS'
  | 'EXTRACT_INFORMATION'
  | 'ANALYZE_CONTEXT'
  | 'BUILD_ACTION_PLAN'
  | 'CREATE_OR_LINK_BILLS';

/**
 * Why a Pipefy webhook card's document could not be attached to the payable the card reserved,
 * so it fell back to manual bill creation. Stored with the values that decided it.
 */
export type PipefyCardRefusalCode =
  /** No PipefyCardIntakeReservation row for the card. */
  | 'PIPEFY_RESERVATION_MISSING'
  /** The card has a reservation, but no bill was attached to it yet. */
  | 'PIPEFY_RESERVATION_WITHOUT_BILL'
  | 'PIPEFY_ENTITY_UNRESOLVED'
  | 'PIPEFY_EXTRACTION_MISSING'
  /** The reservation points to a bill that no longer exists for the tenant. */
  | 'PIPEFY_RESERVED_BILL_NOT_FOUND'
  /** The reserved bill was deleted. */
  | 'PIPEFY_RESERVED_BILL_DELETED'
  /** The reserved bill belongs to an entity other than the document's resolved entity. */
  | 'PIPEFY_RESERVED_BILL_ENTITY_MISMATCH'
  /** The reserved bill left DRAFT (submitted, approved, paid...), so it cannot adopt documents. */
  | 'PIPEFY_RESERVED_BILL_NOT_DRAFT';

/** The reserved bill as the routing read it before executing, or null when there is none. */
export interface PipefyReservedBillState {
  found: boolean;
  entityId: string | null;
  billStatus: string | null;
  deleted: boolean;
  /** A user other than the bill's creator changed it: documents attach for review only. */
  manuallyEdited: boolean;
}

export interface PipefyCardRefusal {
  code: PipefyCardRefusalCode;
  cardId: string;
  /** Whether a reservation row exists for the card, and its status (PENDING / COMPLETED). */
  reservationExists: boolean;
  reservationStatus: string | null;
  reservedBillId: string | null;
  resolvedEntityId: string | null;
  hasExtraction: boolean;
  reservedBill: PipefyReservedBillState | null;
}

/**
 * A Pipefy card document attached to its reserved bill for review only, because someone edited
 * the draft by hand. Stored on the document under `pipefyReservedBillReview`.
 */
export interface PipefyReservedBillReview {
  code: 'PIPEFY_RESERVED_BILL_MANUALLY_EDITED';
  cardId: string;
  reservedBillId: string;
  resolvedEntityId: string | null;
  reservedBill: PipefyReservedBillState;
}

/** Document metadata key holding a {@link PipefyReservedBillReview}. */
export const PIPEFY_RESERVED_BILL_REVIEW_METADATA_KEY = 'pipefyReservedBillReview';

/** A document the bundle assignment could not settle, as stored on the processing plan. */
export interface BundleAssignmentFailure {
  documentId: string;
  error: string;
  pipefyCardRefusal?: PipefyCardRefusal;
}

export interface BundleProcessingStepFailure {
  documentId: string;
  documentName: string;
  code?: BundlePreassignmentFailureCode;
  details?: { registeredCountry?: string; documentCountry?: string };
  reason: string;
}

export interface BundleProcessingStepSummary {
  name: BundleProcessingStepName;
  status: BundleProcessingStepStatus;
  summary?: string;
  failures?: BundleProcessingStepFailure[];
  startedAt?: string;
  completedAt?: string;
}

export interface BundleProcessingItem {
  bundleId: string;
  entityId: string;
  submissionText?: string;
  status: BundleProcessingUiStatus;
  planStatus: BundlePlanBundleStatus;
  documents: BundleProcessingDocumentSummary[];
  steps: BundleProcessingStepSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface BundleProcessingStatusResponse {
  bundles: BundleProcessingItem[];
  totalProcessing: number;
  totalCompletedRecent: number;
  totalFailed: number;
  /** Opaque cursor to pass as the first `cursor` to the completed-bundles endpoint. Null when all terminal bundles fit in this response. */
  completedCursor: string | null;
}

export interface CompletedBundlesResponse {
  bundles: BundleProcessingItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CancelBundleProcessingResponse {
  cancelled: boolean;
  bundleId: string;
  cancelledDocuments: number;
}

export function isBundleProcessingPlanV2(value: unknown): value is BundleProcessingPlan {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  if (v.version !== 2) {
    return false;
  }
  if (!Array.isArray(v.items)) {
    return false;
  }
  if (!v.context || typeof v.context !== 'object') {
    return false;
  }
  const ctx = v.context as Record<string, unknown>;
  if (ctx.status !== 'PENDING' && ctx.status !== 'DONE') {
    return false;
  }
  return typeof v.createdAt === 'string' && typeof v.updatedAt === 'string';
}
